import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Modal } from "@/shared/ui/modal"
import { yupResolver } from "@hookform/resolvers/yup"
import { SubmitHandler, useForm } from "react-hook-form"
import { FormInput } from "@/shared/ui/form-input"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { closeUserAccrualModal, fetchUserAccrual, selectIsUserAccrualModalOpen, selectOnAccrual, userAccrualValidationSchema } from "../../model"
import { fromNanoToUnit, selectAvailableBonuses, toNano } from "@/entities/bonus"
import { useMemo } from "react"
import BonusSvg from '@/shared/assets/images/BonusMenuIcon.svg'
import { fetchWallets, selectMainWallet } from "@/entities/community"
import TransferSvg from '@/shared/assets/images/ArrowsLeftRight.svg'

interface UserAccrualFormValues {
    bonus_id: string,
    amount: number,
    comment: string
}

const initialState: UserAccrualFormValues = {
    bonus_id: "",
    amount: 0,
    comment: ""
}

export const UserAccrualModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsUserAccrualModalOpen)
    const onAccrual = useAppSelector(selectOnAccrual)
    const bonuses = useAppSelector(selectAvailableBonuses)
    const wallet = useAppSelector(selectMainWallet)

    const bonusSelectOptions = useMemo(() => bonuses.map((bonus) => ({
        value: bonus.id,
        label: bonus.name
    })), [bonuses])

    const handleClose = () => {
        if (!isSubmitting) dispatch(closeUserAccrualModal())
    }

    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        try {
            await dispatch(fetchUserAccrual({
                bonus_id: data.bonus_id,
                amountNano: toNano(data.amount),
                comment: data.comment
            })).unwrap()
        } catch (error) {
            notify.error(getErrorMessage(error))
            return
        }

        let refreshFailed = false
        try {
            await dispatch(fetchWallets()).unwrap()
        } catch {
            refreshFailed = true
        }
        notify.success('Бонусы успешно начислены')
        if (refreshFailed) notify.error('Бонусы начислены, но баланс не удалось обновить')
        reset()
        onAccrual?.()
        dispatch(closeUserAccrualModal())
    }

    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors, isSubmitting },
        reset,
        getValues
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(userAccrualValidationSchema)
    })

    const calculateAccrual = useMemo(() => {
        const walletBalance = wallet[getValues('bonus_id')] ?? 0
        const isEnought = (walletBalance > 0) && (walletBalance >= toNano(getValues('amount')))
        const afterAccrual = walletBalance - toNano(getValues('amount'))
        return {
            isEnought,
            afterAccrual
        }
    }, [wallet, watch('amount'), watch('bonus_id')])

    return (
        <Modal
            isOpen={isOpen}
            title="Начисление бонусов"
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <FormInput
                    label={"Бонус"}
                    id={"bonus_id"}
                    options={bonusSelectOptions}
                    control={control}
                    error={errors['bonus_id']}
                    type="select"
                    required={true}
                    placeholder="Выберите бонус"
                    components={{
                        ValueContainer: (props) => {
                            return (
                                <>
                                    {props.hasValue &&
                                        <div className="w-8 h-8 flex justify-center mr-2 items-center rounded-full overflow-hidden">
                                            <img src={bonuses?.find((bonus) => bonus.id === props.getValue()?.[0]?.value)?.image} className="w-full object-contain h-full" />
                                        </div>
                                    }
                                    {props.children}
                                </>
                            )
                        }
                    }}
                />
                <FormInput
                    register={register}
                    label={"Количество бонусов"}
                    id={"amount"}
                    type="number"
                    error={errors['amount']}
                    required={true}
                    placeholder="Укажите количество бонусов"
                />
                <div className="mt-[12px] mb-1 flex justify-between items-center">
                    <div className="flex rounded-full bg-color-dark py-[6px] px-3 w-full items-center gap-[6px]">
                        <BonusSvg className="fill-color-dark text-color-primary" width={24} height={24} />
                        <div className="font-semibold text-white text-base">
                            {fromNanoToUnit(wallet[watch('bonus_id')] ?? 0)}
                        </div>
                    </div>
                    <TransferSvg width={32} height={32} className="mx-3" />
                    <div className={`flex rounded-full py-[6px] px-3 transition w-full items-center gap-[6px] ${calculateAccrual.isEnought ? 'bg-color-dark' : 'bg-color-danger'}`}>
                        <BonusSvg className={`transition ${calculateAccrual.isEnought ? 'text-color-primary fill-color-dark' : 'text-white fill-color-danger'}`} width={24} height={24} />
                        <div className="font-semibold text-white text-base">
                            {fromNanoToUnit(calculateAccrual.afterAccrual)}
                        </div>
                    </div>
                </div>
                <FormInput
                    register={register}
                    label={"Сообщение к начислению (опционально)"}
                    id={"comment"}
                    error={errors['comment']}
                    required={false}
                    placeholder="Введите ваше сообщение"
                />
                <div className="w-full flex gap-3">
                    <button type="button" onClick={handleClose} disabled={isSubmitting} className="form_cancel_button">
                        Отменить
                    </button>
                    <button disabled={isSubmitting} className="form_handle_button" type="submit">
                        Начислить
                    </button>
                </div>
            </form>
        </Modal>
    )
}